import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import API_BASE from "../../config";
import { extractErrorMessage } from "../../utils/apiError";
import MarkdownEditor from "../../components/MarkdownEditor";
import { SECTIONS, DEFAULTS, keysInSection } from "./siteContent";
import s from "./SiteContentPage.module.css";

function getUser() {
  try {
    return JSON.parse(sessionStorage.getItem("user") || localStorage.getItem("user") || "null");
  } catch { return null; }
}

function authHeaders() {
  const u = getUser();
  return {
    "Content-Type": "application/json",
    "X-User-Role":  u?.role || "",
    "X-Username":   u?.username || "",
  };
}

export default function SiteContentPage() {
  const { section: sectionKey } = useParams();
  const section = SECTIONS[sectionKey];

  if (!section) {
    return <Navigate to="/admin/site-content/homepage" replace />;
  }

  return <SectionEditor sectionKey={sectionKey} section={section} />;
}

function SectionEditor({ sectionKey, section }) {
  const [values,  setValues]  = useState(() => seedValues(sectionKey, {}));
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState("");
  const [flash,   setFlash]   = useState("");

  useEffect(() => {
    setLoading(true);
    setError("");
    setFlash("");
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/site-content`);
        const data = res.ok ? await res.json() : {};
        setValues(seedValues(sectionKey, data));
      } catch {
        setValues(seedValues(sectionKey, {}));
      } finally {
        setLoading(false);
      }
    })();
  }, [sectionKey]);

  function set(key, v) {
    setValues(prev => ({ ...prev, [key]: v }));
  }

  async function save() {
    setBusy(true);
    setError("");
    setFlash("");
    try {
      const res = await fetch(`${API_BASE}/api/site-content`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(values),
      });
      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, `Save failed (${res.status})`));
      }
      const saved = await res.json();
      setValues(seedValues(sectionKey, saved));
      setFlash("Saved. Public homepage updated.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  function resetToDefaults() {
    if (!window.confirm(`Reset ${section.title} to defaults? Your current text in this section will be cleared.`)) return;
    setValues(seedValues(sectionKey, {}));
    setFlash("Defaults restored — click Save to publish.");
  }

  return (
    <div className={s.wrap}>
      <div className={s.header}>
        <div className={s.eyebrow}>{section.eyebrow}</div>
        <h1 className={s.title}>{section.title}</h1>
        <p className={s.sub}>{section.description}</p>
      </div>

      {loading ? (
        <div className={s.state}>Loading…</div>
      ) : (
        <>
          {section.groups.map(group => (
            <section key={group.heading} className={s.card}>
              <h2 className={s.cardTitle}>{group.heading}</h2>
              <div className={s.fields}>
                {group.items.map(f => (
                  <label key={f.key} className={s.field}>
                    <span className={s.fieldLabel}>{f.label}</span>
                    {f.rich ? (
                      <MarkdownEditor
                        rows={f.rows || 6}
                        value={values[f.key] ?? ""}
                        onChange={v => set(f.key, v)}
                        placeholder={DEFAULTS[f.key] || ""}
                      />
                    ) : f.multiline ? (
                      <textarea
                        rows={f.rows || 3}
                        value={values[f.key] ?? ""}
                        onChange={e => set(f.key, e.target.value)}
                        placeholder={DEFAULTS[f.key] || ""}
                      />
                    ) : (
                      <input
                        value={values[f.key] ?? ""}
                        onChange={e => set(f.key, e.target.value)}
                        placeholder={DEFAULTS[f.key] || ""}
                      />
                    )}
                    {f.hint && <span className={s.fieldHint}>{f.hint}</span>}
                  </label>
                ))}
              </div>
            </section>
          ))}

          <div className={s.footer}>
            <button className={s.primaryBtn} onClick={save} disabled={busy}>
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button className={s.resetBtn} onClick={resetToDefaults} disabled={busy}>
              Reset to defaults
            </button>
            {flash && <span className={s.flash}>{flash}</span>}
            {error && <span className={s.errorText}>{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

/** Starts every key in the section as an empty string (so inputs are always controlled). */
function seedValues(sectionKey, src) {
  const out = {};
  for (const key of keysInSection(sectionKey)) {
    out[key] = src[key] ?? "";
  }
  return out;
}
